import { useCallback, useEffect, useState } from 'react';
import { Button, Modal, Stack } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import moment from 'moment';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import CustomModal from 'src/components/custom_components/CustomModal/CustomModal';
import WarnNoConnectedCameraModal from 'src/components/custom_components/WarnNoConnectedCameraModal';
import { ResumeInformationModal } from 'src/components/ResumeInformationModal';

import { Api } from 'src/models/Api.model';
import { ApplicationInstance } from 'src/models/Application.model';
import { DeviceDataView } from 'src/models/GalenData/DeviceDataView.model';
import { OrderData } from 'src/models/GalenData/Order.model';
import { GalenDataUser } from 'src/models/GalenData/User.model';
import { I18NextNS } from 'src/models/Language.model';
import { ReportTemplate } from 'src/models/ReportTemplate.model';
import { Session } from 'src/models/Session.model';

import useCheckCameraConnection from 'src/hooks/useCheckCameraConnection';
import useGetExistingSiteData from 'src/hooks/useGetExistingSiteData';
import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';
import useNavigateAfterConfirm from 'src/hooks/useNavigateAfterConfirm';
import useSubmitSession from 'src/hooks/useSubmitSession';

import { getMatchedPatientWithOpenOrder } from 'src/utils/patientMatching';
import {
  validateNameLength,
  validateNamePattern,
  validatePID,
  validatePIDLength,
} from 'src/utils/validationRules';

import { SearchPatientFormValues } from '../SearchPatientInfoFormGroup/SearchPatientInfoFormGroup';
import MatchedFound from './MatchedFound';
import NewOrderCreationForm from './NewOrderCreationForm';

type NewPatientCreationProps = {
  isOpen: boolean;
  onClose: () => void;
  initialValues?: SearchPatientFormValues;
};

type FormValues = {
  dob: string;
  firstName: string;
  lastName: string;
  id: string;
  template: ReportTemplate;
  siteId: string;
  siteName: string;
};

export default function NewPatientCreation({
  isOpen,
  onClose,
  initialValues,
}: NewPatientCreationProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation(I18NextNS.COMMON);

  const {
    practiceSettings: { product },
    updateSession,
  } = useRemoteBoundStore();

  const { getExistingSiteData } = useGetExistingSiteData();

  const { createPatient, initializeRetinalExam } = useSubmitSession();

  const { handleHttpRequestError } = useHandleHttpRequestError();
  const navigateAfterConfirm = useNavigateAfterConfirm();
  const { isCameraConnected } = useCheckCameraConnection();

  const [matchedPatient, setMatchedPatient] = useState<GalenDataUser>();
  const [openOrder, setOpenOrder] = useState<DeviceDataView<OrderData>>();
  const [showCameraWarning, setShowCameraWarning] = useState(false);
  const [currentSession, setCurrentSession] = useState<Session>();
  const [isShowResumeModal, setIsShowResumeModal] = useState(false);

  const handleCloseAndReset = () => {
    onClose();
    setMatchedPatient(undefined);
    setOpenOrder(undefined);
    setCurrentSession(undefined);
    setIsShowResumeModal(false);
    reset();
  };

  const getValidatedDefaultValue = (
    value: string | undefined,
    validator: (val: string) => boolean,
  ): string => {
    if (!value) return '';
    return validator(value) ? value : '';
  };

  const formMethods = useForm<FormValues>({
    defaultValues: {
      dob: '',
      firstName: '',
      lastName: '',
      id: '',
      template: ApplicationInstance(product).templates[0],
      siteId: '',
      siteName: '',
    },
  });

  const {
    handleSubmit,
    getValues,
    reset,
    setValue,
    formState: { isSubmitting },
  } = formMethods;

  useEffect(() => {
    getExistingSiteData();

    const dob = initialValues?.dob;
    const firstName = getValidatedDefaultValue(
      initialValues?.firstName,
      (val) => validateNamePattern(val) && validateNameLength(val),
    );
    const lastName = getValidatedDefaultValue(
      initialValues?.lastName,
      (val) => validateNamePattern(val) && validateNameLength(val),
    );
    const id = getValidatedDefaultValue(
      initialValues?.id,
      (val) => validatePID(val) && validatePIDLength(val),
    );

    if (dob) setValue('dob', dob);
    if (firstName) setValue('firstName', firstName);
    if (lastName) setValue('lastName', lastName);
    if (id) setValue('id', id);
  }, [getExistingSiteData, initialValues, setValue]);

  const handleCreateWithAction = async (postSubmitAction?: () => void) => {
    if (postSubmitAction === navigateAfterConfirm && !isCameraConnected) {
      setShowCameraWarning(true);
      return;
    }

    await handleSubmit(async (data) => {
      await onSubmit(data, postSubmitAction);
    })();
  };

  const onSubmit = useCallback(
    async (data: FormValues, postSubmitAction?: () => void) => {
      try {
        const { firstName, lastName, dob, id, siteId, siteName } = data;

        const trimmedFirstName = firstName.trim();
        const trimmedLastName = lastName.trim();
        const trimmedId = id.trim();

        const formattedDob = moment(dob).format('YYYY-MM-DD');
        const age = moment().diff(dob, 'years');
        const { matchedPatient, openOrder } =
          await getMatchedPatientWithOpenOrder(data);

        if (matchedPatient && openOrder) {
          setMatchedPatient(matchedPatient);
          setOpenOrder(openOrder);
          return;
        }

        const patientUserId = matchedPatient
          ? matchedPatient.userId
          : await createPatient({
              firstName: trimmedFirstName,
              lastName: trimmedLastName,
              dob: formattedDob,
              id: trimmedId,
              siteId,
            });

        const tempSession = {
          patientUserId,
          id: trimmedId,
          firstName: trimmedFirstName,
          lastName: trimmedLastName,
          dob: formattedDob,
          age,
          template: getValues('template'),
          siteId,
          siteName,
        };

        updateSession(tempSession);

        await initializeRetinalExam(tempSession);
        postSubmitAction?.();
      } catch (error) {
        const errorMessage = handleHttpRequestError(error);
        Api.alertBox({
          title: t('bootbox.title.Error'),
          message: errorMessage,
          locale: language,
          type: 'error',
        });
      }
    },
    [
      createPatient,
      getValues,
      updateSession,
      initializeRetinalExam,
      handleHttpRequestError,
      t,
      language,
    ],
  );

  return (
    <>
      <CustomModal
        show={isOpen && !isShowResumeModal}
        onHide={handleCloseAndReset}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {openOrder
              ? t('patientFields.newOrderCreationMatchFound')
              : t('patientFields.newOrderCreation')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {matchedPatient && openOrder ? (
            <MatchedFound
              matchedPatient={matchedPatient}
              openOrder={openOrder}
              onGoBack={() => {
                setMatchedPatient(undefined);
                setOpenOrder(undefined);
              }}
              onResumedExam={(session) => {
                setCurrentSession(session);
                setIsShowResumeModal(true);
              }}
            />
          ) : (
            <FormProvider {...formMethods}>
              <NewOrderCreationForm onSubmit={onSubmit} />
            </FormProvider>
          )}
        </Modal.Body>
        {!openOrder && (
          <Modal.Footer className="d-flex justify-content-between">
            <Button
              variant="primary-100"
              onClick={handleCloseAndReset}
              disabled={isSubmitting}
            >
              {t('modal.cancel')}
            </Button>
            <Stack direction="horizontal" gap={2}>
              <Button
                variant="primary-100"
                onClick={() => handleCreateWithAction(reset)}
                disabled={isSubmitting}
              >
                {t('modal.saveAndCreatePatient')}
              </Button>

              <Button
                variant="primary"
                onClick={() => handleCreateWithAction(navigateAfterConfirm)}
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? t('modal.confirming')
                  : t('modal.proceedToExam')}
              </Button>
            </Stack>
          </Modal.Footer>
        )}

        <WarnNoConnectedCameraModal isVisible={showCameraWarning} />
      </CustomModal>

      {currentSession && (
        <ResumeInformationModal
          currentSession={currentSession}
          isShowResumeModal={isShowResumeModal}
          handleSetIsShowResumeModal={(show) => {
            setIsShowResumeModal(show);
            if (!show) {
              handleCloseAndReset();
            }
          }}
        />
      )}
    </>
  );
}
